import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import { courseLozengeColours } from "~/components/general/CoursesDropdown";
import CoursesHomeLink from "~/components/general/CoursesHomeLink";
import Input from "~/components/parts/Input";
import Page from "~/components/parts/Page";
import CourseCard from "./CourseCard";
import { cn } from "~/utils";
import {
  posthogEventBrowseCoursesLeftWithoutSelection,
  posthogEventCourseViewed,
} from "~/utils/posthog/courses";
import { defaultSelectComponentStyles } from "~/utils/styles";

const BrowseCourses = ({
  addMode = false,
  authors = [],
  isLoading = false,
  publishedCourses = [],
  preAddedCourseIds = [],
  user = null,
}) => {
  // Check if the user can add and adapt courses from this page.
  const userCanAddAndAdapt = ["teacher", "trust", "admin"].some(
    (role) => role === user?.type,
  );

  // Set up the state required for filtering.
  const [search, setSearch] = useState("");
  const [publishers, setPublishers] = useState([]);
  const [types, setTypes] = useState([]);

  // Filter the courses according to the current filter state.
  const filteredCourses = publishedCourses.filter((course) => {
    const searchCourseNameMatch = search
      ? course.name?.toLowerCase()?.includes(search.toLowerCase())
      : true;
    const searchAuthorNameMatch = search
      ? course.author?.name?.toLowerCase()?.includes(search.toLowerCase())
      : true;
    const searchMatch = searchCourseNameMatch || searchAuthorNameMatch;
    const publisherMatch =
      publishers.length > 0
        ? publishers.some(({ value }) => value === course.author?.sid)
        : true;
    const typeMatch =
      types.length > 0
        ? types.some(({ value }) => value === course.type)
        : true;
    return searchMatch && publisherMatch && typeMatch;
  });

  // Select component options for publisher.
  const publisherOptions = authors.map((author) => ({
    value: author.sid,
    label: author.name,
  }));

  // Select component options for type.
  const typeOptions = [
    {
      value: "SPECIFICATION",
      label: "Specification",
      backgroundColor: "#fefce8",
    },
    { value: "CURRICULUM", label: "Curriculum", backgroundColor: "#faf5ff" },
    {
      value: "SCHEME_OF_WORK",
      label: "Scheme of Learning",
      backgroundColor: "#f0fdf4",
    },
  ];

  const handleCourseClick = (course, position) => {
    posthogEventCourseViewed({
      triggerId: "browse-courses-course-card",
      course: { ...course, authorName: course.author?.name },
      browseCoursesContext: {
        count: filteredCourses.length,
        filters: {
          text: search,
          publisher: publishers.map(({ label }) => label),
          type: types.map(({ value }) => value),
        },
        positionSelected: position,
      },
    });
    window.removeEventListener("beforeunload", handleBeforeUnload);
    window.location = `/explorer.php?coid=${course.coid}${addMode ? "&mode=preview" : ""}`;
  };

  /**
   * This section of code is responsible for tracking when a user leaves
   * the page without selecting a course.
   */

  // Use refs to store the latest values for dependencies.
  const eventPropertiesRef = useRef({
    count: filteredCourses.length,
    filters: {
      text: search,
      publisher: publishers.map(({ label }) => label),
      type: types.map(({ value }) => value),
    },
  });

  // Define handleBeforeUnload using the ref values.
  const handleBeforeUnload = useCallback(() => {
    posthogEventBrowseCoursesLeftWithoutSelection(eventPropertiesRef.current);
  }, []);

  // Update the ref on dependency change without recreating handleBeforeUnload.
  useEffect(() => {
    eventPropertiesRef.current = {
      count: filteredCourses.length,
      filters: {
        text: search,
        publisher: publishers.map(({ label }) => label),
        type: types.map(({ value }) => value),
      },
    };
  }, [filteredCourses.length, search, publishers, types]);

  // Add the event listener on mount and remove it on unmount.
  useEffect(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  return (
    <>
      {addMode && (
        <>
          {createPortal(
            <div className="z-5 fixed top-[57px] m-0 flex h-[80px] w-full items-center justify-center border-b border-gray-100 bg-white p-3 text-sm shadow-sm xs:h-[45px]">
              <p className="text-center font-bold text-gray-700">
                Choose a course or{" "}
                <a
                  className="text-nowrap text-gray-700 underline decoration-2 underline-offset-4 hover:decoration-[3px]"
                  href="/manage-courses.php"
                >
                  go back to manage courses
                </a>
              </p>
            </div>,
            document.body,
          )}
          <div className="h-[80px] xs:h-[45px]"></div>
        </>
      )}
      <Page
        className={cn("relative", !addMode && "max-md:gap-4")}
        title="Published Courses"
        subtitle="A catalogue of all the courses we have crafted to align with various curricula."
      >
        {!addMode && (
          <div className="flex justify-end md:absolute md:right-8 md:top-8 lg:right-12 lg:top-12">
            <CoursesHomeLink posthogTriggerId="browse-courses-courses-home-button" />
          </div>
        )}

        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Input
              className="min-h-12 !rounded-md !border-gray-300 !p-2 text-sm outline-0 placeholder:text-gray-400 focus:shadow-[0_0_0_1px_#9ca3af]"
              disabled={isLoading}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name..."
              type="search"
              value={search}
            />

            <Select
              isClearable
              isDisabled={isLoading}
              isMulti
              isSearchable={false}
              onChange={(arr) => setPublishers(arr)}
              options={publisherOptions}
              placeholder="Select publisher..."
              styles={defaultSelectComponentStyles}
              value={publishers}
            />

            <Select
              isClearable
              isDisabled={isLoading}
              isMulti
              isSearchable={false}
              onChange={(arr) => setTypes(arr)}
              options={typeOptions}
              placeholder="Select type..."
              styles={{
                ...defaultSelectComponentStyles,
                multiValue: (base, { data }) => ({
                  ...base,
                  backgroundColor: courseLozengeColours[data.value],
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: "white",
                  fontWeight: 600,
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: "white",
                  ":hover": {
                    backgroundColor: "white",
                    color: "black",
                    opacity: 0.5,
                  },
                }),
                option: (base, { data, isFocused }) => ({
                  ...base,
                  backgroundColor: isFocused ? data.backgroundColor : undefined,
                  ":active": {
                    ...base[":active"],
                    backgroundColor: data.backgroundColor,
                  },
                }),
              }}
              value={types}
            />
          </div>

          {!isLoading && filteredCourses.length === 0 ? (
            <div className="py-6 text-center text-xl font-semibold text-gray-400">
              No courses found
            </div>
          ) : (
            <div
              className={cn(
                "px-2 text-center text-sm text-gray-400",
                isLoading && "opacity-0",
              )}
            >
              {`${filteredCourses.length} course${filteredCourses.length === 1 ? "" : "s"}`}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 md:auto-rows-fr md:grid-cols-2 lg:grid-cols-3">
            {isLoading &&
              new Array(6)
                .fill()
                .map((_, i) => (
                  <div
                    key={i}
                    className="h-28 animate-pulse rounded-md bg-slate-100 duration-75 md:h-36"
                  />
                ))}
            {!isLoading &&
              filteredCourses.map((course, index) => {
                const courseAlreadyAdded = preAddedCourseIds.some(
                  (coid) => coid === course.coid,
                );
                const courseCardDisabled = addMode && courseAlreadyAdded;
                return (
                  <CourseCard
                    key={`course-${course.coid}`}
                    className={cn(
                      "",
                      courseCardDisabled && "cursor-auto opacity-40",
                    )}
                    course={{
                      ...course,
                      authorName: course.author.name,
                      thumb: course.author.thumb,
                    }}
                    courseAlreadyAdded={
                      userCanAddAndAdapt && courseAlreadyAdded
                    }
                    onClick={() => {
                      if (!courseCardDisabled) {
                        handleCourseClick(course, index + 1);
                      }
                    }}
                  />
                );
              })}
          </div>
        </div>
      </Page>
    </>
  );
};

export default BrowseCourses;
